import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { ip_back } from "../../config";
import { canAccessGoalProposals } from "../../utils/goalProposalsAccess";

const CATEGORY_LABELS = {
  1: "Health",
  2: "Recreational",
  3: "Competitive",
};

export default function GoalProposals() {
  const { t } = useTranslation();
  const info = JSON.parse(localStorage.getItem("user") || "{}");
  const allowed = canAccessGoalProposals(info);

  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [approveDialog, setApproveDialog] = useState(null);
  const [approveAction, setApproveAction] = useState("create_new");
  const [catalogAttributes, setCatalogAttributes] = useState([]);
  const [selectedAttributeId, setSelectedAttributeId] = useState("");
  const [newAttributeName, setNewAttributeName] = useState("");

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  const loadProposals = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${ip_back.replace(/\/$/, "")}/api/admin/goal-proposals?status=pending`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load proposals");
      }
      setProposals(data.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  if (!allowed) {
    return <Navigate to="/Dashboard" replace />;
  }

  const loadCatalogForCategory = async (categoryId) => {
    const res = await fetch(
      `${ip_back.replace(/\/$/, "")}/api/admin/goal-proposals/attributes?categoryId=${categoryId}`,
      { headers: authHeaders() }
    );
    const data = await res.json();
    if (data.success) {
      setCatalogAttributes(data.data || []);
    }
  };

  const openApprove = async (proposal) => {
    setApproveDialog(proposal);
    setApproveAction("create_new");
    setNewAttributeName(proposal.custom_text);
    setSelectedAttributeId("");
    await loadCatalogForCategory(proposal.category_id);
  };

  const handleApprove = async () => {
    if (!approveDialog) return;
    const body =
      approveAction === "map_existing"
        ? { action: "map_existing", attributeId: Number(selectedAttributeId) }
        : { action: "create_new", attributeName: newAttributeName.trim() || approveDialog.custom_text };

    try {
      const res = await fetch(
        `${ip_back.replace(/\/$/, "")}/api/admin/goal-proposals/${approveDialog.proposal_id}/approve`,
        { method: "POST", headers: authHeaders(), body: JSON.stringify(body) }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Approve failed");
      }
      setSnack({ open: true, message: t("goalProposalApproved") || "Goal approved", severity: "success" });
      setApproveDialog(null);
      loadProposals();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    }
  };

  const handleReject = async (proposalId) => {
    try {
      const res = await fetch(
        `${ip_back.replace(/\/$/, "")}/api/admin/goal-proposals/${proposalId}/reject`,
        { method: "POST", headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Reject failed");
      }
      setSnack({ open: true, message: t("goalProposalRejected") || "Goal rejected", severity: "info" });
      loadProposals();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t("goalProposalsTitle") || "Custom Goal Proposals"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("goalProposalsSubtitle") ||
            "Review player-written training goals before adding them to the catalog."}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Typography>{t("loading") || "Loading…"}</Typography>
        ) : proposals.length === 0 ? (
          <Alert severity="info">{t("goalProposalsEmpty") || "No pending proposals."}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("customObjectiveLabel") || "Goal text"}</TableCell>
                <TableCell>{t("category") || "Category"}</TableCell>
                <TableCell>{t("type") || "Type"}</TableCell>
                <TableCell>{t("players") || "Players"}</TableCell>
                <TableCell align="right">{t("actions") || "Actions"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals.map((p) => (
                <TableRow key={`${p.proposal_id}-${p.normalized_text}`}>
                  <TableCell>{p.custom_text}</TableCell>
                  <TableCell>{CATEGORY_LABELS[p.category_id] || p.category_id}</TableCell>
                  <TableCell>{p.focus_type}</TableCell>
                  <TableCell>{p.user_count}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openApprove(p)} sx={{ mr: 1 }}>
                      {t("approve") || "Approve"}
                    </Button>
                    <Button size="small" color="error" onClick={() => handleReject(p.proposal_id)}>
                      {t("reject") || "Reject"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("approveGoalProposal") || "Approve custom goal"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>{t("approvalAction") || "Action"}</InputLabel>
            <Select
              value={approveAction}
              label={t("approvalAction") || "Action"}
              onChange={(e) => setApproveAction(e.target.value)}
            >
              <MenuItem value="create_new">{t("createNewGoal") || "Add as new catalog goal"}</MenuItem>
              <MenuItem value="map_existing">{t("mapExistingGoal") || "Map to existing goal"}</MenuItem>
            </Select>
          </FormControl>
          {approveAction === "create_new" ? (
            <TextField
              fullWidth
              label={t("goalDisplayName") || "Display name"}
              value={newAttributeName}
              onChange={(e) => setNewAttributeName(e.target.value)}
            />
          ) : (
            <FormControl fullWidth>
              <InputLabel>{t("existingGoal") || "Existing goal"}</InputLabel>
              <Select
                value={selectedAttributeId}
                label={t("existingGoal") || "Existing goal"}
                onChange={(e) => setSelectedAttributeId(e.target.value)}
              >
                {catalogAttributes.map((a) => (
                  <MenuItem key={a.attribute_id} value={a.attribute_id}>
                    {a.translated_name || a.attribute_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>{t("cancel") || "Cancel"}</Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={approveAction === "map_existing" && !selectedAttributeId}
          >
            {t("confirm") || "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </AdminLayout>
  );
}
